import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Client, ClientMergePreview, Organization} from '../../interfaces/client';
import {ClientService} from '../../shared/client.service';
import {OrganizationService, OrganizationSearchResult} from '../../shared/organization.service';
import {AuthService} from '../../shared/auth.service';

/**
 * Embedded section on the Client detail page. Surfaces the canonical name,
 * aliases, and scraped-vacancy count from the linked Organization, and lets the
 * link itself be set, corrected, or cleared.
 *
 * The card used to render only when Client.Organization_id was already set,
 * which hid it from exactly the clients that needed it: a client created before
 * the catalogue existed had no link and therefore no way to get one. Measured
 * on the Verholt tenant, that was 10 clients with 8 catalogue matches waiting.
 *
 * The link is editable rather than write-once because the typeahead can return
 * a confidently wrong sibling organisation. The backend carries the guardrails
 * (tenant policy, at-most-one per tenant, ChangeLog on every change); this
 * component only has to surface the 422 when the organisation is taken.
 *
 * A taken organisation almost always means the same company was entered twice
 * (#1849: Waterschap Rivierenland as 19 and 26). The 422 names the client that
 * holds it, so the card offers to merge that client into this one instead of
 * leaving a dead end that only a developer could clear. Admins only, as the
 * server enforces: the other client is deleted.
 *
 * Engagement summary (interest events) is deferred to a follow-up per the
 * locked v1 architecture; v1 ships counts only.
 */
@Component({
    selector: 'bst-client-organization-section',
    templateUrl: './client-organization-section.component.html',
    styleUrls: ['./client-organization-section.component.scss'],
    standalone: false,
})
export class ClientOrganizationSectionComponent implements OnChanges {

    @Input() client: Client | null = null;
    /** After a merge: projects, opportunities and contacts moved in, so the page must refetch. */
    @Output() merged = new EventEmitter<void>();
    public org: Organization | null = null;
    public loading = false;
    public errorMessage: string | null = null;

    public picking = false;
    public saving = false;
    public picked: OrganizationSearchResult | null = null;
    public pickerControl = new FormControl<OrganizationSearchResult | null>(null);

    /** The client that already holds the picked organisation, with what a merge would move. */
    public mergeOffer: ClientMergePreview | null = null;
    public confirmingMerge = false;

    constructor(
        private orgService: OrganizationService,
        private clientService: ClientService,
        private auth: AuthService,
    ) {
        this.pickerControl.valueChanges.subscribe((selection) => {
            // bst-list-adder in single mode emits a single object (or null on
            // clear); in multi mode it would emit an array. Handle both, as the
            // new-client dialog does.
            const v = Array.isArray(selection) ? selection[0] ?? null : selection ?? null;
            this.picked = v as OrganizationSearchResult | null;
            this.errorMessage = null;
        });
    }

    /**
     * The tenant root is the tenant itself and has no catalogue counterpart, so
     * it is offered neither the link nor the picker. Matches the exclusion
     * crm:pipeline-health applies when counting unlinked clients.
     */
    public get linkable(): boolean {
        return !!this.client?.id && !this.client.is_tenant_root;
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        await this.loadOrg();
    }

    private async loadOrg(): Promise<void> {
        const orgId = this.client?.Organization_id;
        if (!orgId) {
            this.org = null;
            return;
        }
        this.loading = true;
        this.errorMessage = null;
        try {
            this.org = await this.orgService.fetch(orgId);
        } catch (e) {
            console.error('organization load failed', e);
            this.errorMessage = 'Failed to load organization details.';
        } finally {
            this.loading = false;
        }
    }

    public get isAdmin(): boolean {
        return this.auth.isAdmin;
    }

    public startPicking(): void {
        this.picking = true;
        this.errorMessage = null;
        this.mergeOffer = null;
        this.pickerControl.setValue(null);
    }

    public cancelPicking(): void {
        this.picking = false;
        this.picked = null;
        this.errorMessage = null;
        this.mergeOffer = null;
        this.confirmingMerge = false;
    }

    /** First click arms, second click merges: the other client is deleted. */
    public async merge(): Promise<void> {
        if (!this.client || !this.mergeOffer || this.saving) { return; }
        if (!this.confirmingMerge) {
            this.confirmingMerge = true;
            return;
        }
        this.saving = true;
        this.errorMessage = null;
        try {
            const merged = await this.clientService.mergeClient(this.client.id, this.mergeOffer.from.id);
            this.client.Organization_id = merged?.Organization_id ?? this.client.Organization_id;
            this.mergeOffer = null;
            this.confirmingMerge = false;
            this.picking = false;
            this.picked = null;
            await this.loadOrg();
            this.merged.emit();
        } catch (err: any) {
            this.errorMessage = err?.error?.message ?? err?.message ?? 'Could not merge the clients.';
            this.confirmingMerge = false;
        } finally {
            this.saving = false;
        }
    }

    public async link(): Promise<void> {
        if (!this.picked) { return; }
        await this.save(this.picked.id);
    }

    public async clear(): Promise<void> {
        await this.save(null);
    }

    /**
     * PUT the new link and repaint from it.
     *
     * Organization_id is written back onto the shared client object rather than
     * emitted: the detail page reads that field in its own template and would
     * otherwise show a stale link until the next reload.
     */
    private async save(organizationId: number | null): Promise<void> {
        if (!this.client || this.saving) { return; }
        this.saving = true;
        this.errorMessage = null;
        this.mergeOffer = null;
        this.confirmingMerge = false;
        try {
            // 422 is silent: when another client holds the organisation, the
            // merge offer below replaces the toast.
            await this.clientService.saveClient(this.client.id, {Organization_id: organizationId} as Partial<Client>, null, [422]);
            this.client.Organization_id = organizationId;
            this.picking = false;
            this.picked = null;
            await this.loadOrg();
        } catch (err: any) {
            const held = err?.error?.data?.held_client;
            if (err?.status === 422 && held?.id) {
                // The preview is admin-only; a non-admin still gets the name, a link and "ask an admin".
                this.mergeOffer = this.isAdmin
                    ? await this.clientService.mergePreview(this.client.id, held.id).catch(() => null)
                    : {from: {id: held.id, name: held.name, Organization_id: organizationId}, refusal: null, summary: null};
            }
            if (!this.mergeOffer) {
                this.errorMessage = err?.error?.message ?? err?.message ?? 'Could not update the organization link.';
            }
        } finally {
            this.saving = false;
        }
    }
}
